import React, { useState } from "react";
import {
  Typography,
  Paper,
  useTheme,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from "@mui/material";
import { useParams } from "react-router-dom";
import {
  useGetAccountCommentsQuery,
  useGetMapInsurersQuery,
  useGetMapRiskParametersQuery,
  useGetMapSummaryQuery,
  useGetOriginationTabQuery,
} from "api/api-maps";
import Summary from "components/Map/Summary/MapSummaries/Summary";
import { useGetCallScheduleQuery } from "api/api-risk";
import { format } from "date-fns";
import { useGetSummariesQuery } from "api/api-compliance";
import Layout from "components/Common/Layout";
import { PERMISSIONS } from "permissions";
import { useAppSelector } from "redux/hooks";
import {
  Assessment as AssessmentIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";

import styles from "./cc-memo.module.css";

import html2pdf from "html2pdf.js";
import { MapContext } from "pages/maps/map";

const tableContainerStyles = {
  borderRadius: "16px",
  overflow: "hidden",
  boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
  border: "1px solid rgba(224, 224, 224, 0.8)",
  mb: 3.5,
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 8px 28px rgba(0,0,0,0.08)",
  },
  "& .MuiTable-root": {
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  "& .MuiTableHead-root": {
    background: "linear-gradient(135deg, #f5f7fa 0%, #eef2f7 100%)",
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    fontWeight: 700,
    color: "#001E61",
    fontSize: "0.95rem",
    letterSpacing: "0.3px",
    padding: "1.25rem",
  },
  "& .MuiTableCell-root": {
    fontSize: "0.95rem",
    lineHeight: 1.6,
    borderBottom: "1px solid rgba(224, 224, 224, 0.6)",
    padding: "1.25rem",
    color: "#2c3e50",
  },
  pageBreakInside: "avoid",
  position: "relative",
};

const MemoContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box
      className={`credit-memo-container ${styles.ccMemoContainer}`}
      sx={{
        width: "98%",
        maxWidth: "1600px",
        mx: "auto",
        py: 2,
        px: { xs: 2, sm: 3, md: 4 },
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          border: "4px solid",
          borderImage: "linear-gradient(135deg, #001E61 0%, #1976d2 100%) 1",
          borderRadius: "16px",
          pointerEvents: "none",
          zIndex: 1,
        },
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          border: "4px solid",
          borderImage: "linear-gradient(135deg, #001E61 0%, #1976d2 100%) 1",
          borderRadius: "16px",
          pointerEvents: "none",
          zIndex: 1,
          boxShadow: "0 0 20px rgba(0, 0, 0, 0.1)",
        },
      }}
    >
      {children}
    </Box>
  );
};

const MemoBanner = ({
  title,
  company,
  id,
}: {
  title: string;
  company?: string;
  id?: string;
}) => {
  return (
    <Box className={styles.memoBanner}>
      <Box>
        <Typography variant="h4" className={styles.memoTitle} gutterBottom>
          {title}
        </Typography>
        <Typography variant="body1" className={styles.memoSubtitle}>
          {company || "Company"} {id ? `• ${id}` : ""}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          bgcolor: "rgba(255,255,255,0.15)",
          borderRadius: "12px",
          px: 2.5,
          py: 1.5,
          backdropFilter: "blur(4px)",
          border: "1px solid rgba(255,255,255,0.2)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, letterSpacing: "0.5px" }}
        >
          {new Date().toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </Typography>
      </Box>
    </Box>
  );
};

// Enhanced SectionCard component
const SectionCard = ({
  title,
  icon,
  children,
  isFirst = false,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  isFirst?: boolean;
}) => {
  return (
    <Box
      className={styles.sectionCard}
      sx={{
        mt: isFirst ? 0 : 2,
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 12px 40px rgba(0,0,0,0.08)",
        },
      }}
    >
      <Box className={styles.sectionHeader}>
        <Box className={styles.sectionIcon}>{icon}</Box>
        <Typography className={styles.sectionTitle}>{title}</Typography>
      </Box>
      <Box className={styles.sectionContent}>{children}</Box>
    </Box>
  );
};

const SectionHeader = ({ title }: { title: string }) => {
  return (
    <Box className={styles.subSectionHeader}>
      <Typography className={styles.subSectionTitle}>{title}</Typography>
    </Box>
  );
};

const ExecutiveSummaryRow = ({
  label,
  value,
  highlighted = false,
}: {
  label: string;
  value: string | undefined;
  highlighted?: boolean;
}) => {
  return (
    <TableRow
      sx={{ bgcolor: highlighted ? "rgba(25, 118, 210, 0.05)" : "inherit" }}
    >
      <TableCell sx={{ fontWeight: 600, width: "30%" }}>{label}</TableCell>
      <TableCell>{value || ""}</TableCell>
    </TableRow>
  );
};

// StyledPaper component
const StyledPaper = ({
  children,
  sx = {},
}: {
  children: React.ReactNode;
  sx?: any;
}) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2, sm: 3 },
      borderRadius: 2,
      width: "98%",
      maxWidth: "1600px",
      mx: "auto",
      my: 2,
      ...sx,
    }}
  >
    {children}
  </Paper>
);

// Main Credit Memo Component
const CreditCommitteeMemo: React.FC = () => {
  const { id = "" } = useParams();
  const theme = useTheme();

  const [isGeneratingDoc, setIsGeneratingDoc] = useState(false);

  // Utility functions
  const stopEvent = React.useCallback((e: React.SyntheticEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
  }, []);

  // Loading and error states
  const loadingComponent = null;
  const errorComponent = null;

  const {
    data: mapData,
    refetch: refetchMapSummary, // This is the refetch function
  } = useGetMapSummaryQuery(id ?? "", { skip: !id });

  // Call this function to fetch the latest data
  const fetchLatestData = async () => {
    try {
      await refetchMapSummary(); // This will fetch fresh data from the server
    } catch (error) {
      console.error("Error fetching latest data:", error);
    }
  };

  // Example: Call fetchLatestData when needed
  React.useEffect(() => {
    if (id) {
      fetchLatestData();
    }
  }, [id]);
  const { data: mapInsurers } = useGetMapInsurersQuery(id ?? "", { skip: !id });
  const { data: callSchedule } = useGetCallScheduleQuery(
    { map_id: id ?? "", account_type: "supplier" },
    { skip: !id }
  );

  const { data: summariesData } = useGetSummariesQuery(id ?? "", { skip: !id });

  const { data: financialIndicators } = useGetMapRiskParametersQuery(
    {
      map_name: id ?? "",
      account_type: "supplier",
      item_type: "financial",
    },
    { skip: !id }
  );
  const mapName = React.useContext(MapContext);

  const { data: tabData } = useGetOriginationTabQuery(mapName ?? "", {
    skip: mapName === undefined,
  });

  const { data: financialIndicatorsBuyer } = useGetMapRiskParametersQuery(
    {
      map_name: id ?? "",
      account_type: "buyer",
      item_type: "financial",
    },
    { skip: !id }
  );

  const dateKeys = financialIndicators ? Object.keys(financialIndicators) : [];
  const dateKeysBuyer = financialIndicatorsBuyer
    ? Object.keys(financialIndicatorsBuyer)
    : [];

  const selectedFields = [
    "Revenue",
    "Gross Profit",
    "GP Margin",
    "EBITDA",
    "EBIT Margin",
    "Net Profit",
    "NP Margin",
    "Cash & equivalent",
    "Trade Receivables",
    "Inventory",
    "Trade Payables",
    "Total Debt",
    "Total Assets",
    "Total Liabilities",
    "Equity",
    "Debt/Equity",
    "DOI",
    "DOR",
    "DOP",
  ];
  const getValue = (date: string, key: string): number => {
    const raw = financialIndicators?.[date]?.[key];
    return typeof raw === "number" ? raw : 0;
  };

  const getDifference = (key: string): number => {
    const val1 = getValue(dateKeys[0], key);
    const val2 = getValue(dateKeys[1], key);
    return ((val2 - val1) / val1) * 100;
  };

  const getValueBuyer = (date: string, key: string): number => {
    const raw = financialIndicatorsBuyer?.[date]?.[key];
    return typeof raw === "number" ? raw : 0;
  };

  const getDifferenceBuyer = (key: string): number => {
    const valBuyer1 = getValueBuyer(dateKeysBuyer[0], key);
    const valBuyer2 = getValueBuyer(dateKeysBuyer[1], key);
    return ((valBuyer2 - valBuyer1) / valBuyer1) * 100;
  };
  const { data: accountComments } = useGetAccountCommentsQuery(
    { map_name: id ?? "", account_type: "supplier" },
    { skip: !id }
  );

  const { data: accountCommentsBuyer } = useGetAccountCommentsQuery(
    { map_name: id ?? "", account_type: "buyer" },
    { skip: !id }
  );

  // Permissions and approval status
  const USER_PERMISSIONS = useAppSelector(
    (state) => state.appState.user_permission
  );

  // Memoize permissions to prevent unnecessary re-renders
  const permissions = React.useMemo(() => {
    // Always enable export button - set isFieldDisabled to false
    const READ_ONLY = USER_PERMISSIONS?.includes(PERMISSIONS.map_view_risk);
    const isFieldDisabled = false;
    return { READ_ONLY, isFieldDisabled };
  }, [USER_PERMISSIONS]);

  // Enhanced export handlers with improved functionality
  const handleExportPDF = React.useCallback(
    (event?: React.MouseEvent) => {
      if (event) {
        stopEvent(event);
      }

      if (id) {
        // Show generating state
        setIsGeneratingDoc(true);

        try {
          // Get the memo content element and cast it properly
          const element = document.querySelector(".credit-memo-container");

          if (!element) {
            throw new Error("Could not find memo content element");
          }

          // Type assertion to HTMLElement
          const memoContent = element as HTMLElement;

          // Simple configuration for html2pdf
          const opt = {
            margin: 10,
            filename: `credit_memo_${id}.pdf`,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: {
              unit: "mm",
              format: "a4",
              orientation: "portrait" as const,
            },
          };

          // Generate PDF
          html2pdf()
            .from(memoContent)
            .set(opt)
            .save()
            .then(() => {
              setTimeout(() => {
                setIsGeneratingDoc(false);
              }, 1000);
            })
            .catch((error: any) => {
              console.error("PDF generation failed:", error);
              alert("Failed to export PDF. Please try again.");
              setIsGeneratingDoc(false);
            });
        } catch (error) {
          console.error("PDF export failed:", error);
          alert("Failed to export PDF. Please try again.");
          setIsGeneratingDoc(false);
        }
      }
    },
    [id, stopEvent]
  );

  let riskRecommendation: {
    credit_limit: string;
    advance_rate: string | null;
    guarantees: string;
    conditions_precedent: string;
    monitoring_schedule: string;
    repayment_history: string;
    stage: string;
    risk_questions: string;
  } = {
    credit_limit: "",
    advance_rate: "",
    guarantees: "",
    conditions_precedent: "",
    monitoring_schedule: "",
    repayment_history: "",
    stage: "",
    risk_questions: "",
  };

  const rawValue = mapData?.risk_recommendation;
  if (typeof rawValue === "string") {
    try {
      riskRecommendation = JSON.parse(rawValue);
    } catch (e) {
      console.error("Invalid JSON for risk_recommendation", e);
    }
  } else if (typeof rawValue === "object" && rawValue !== null) {
    riskRecommendation = rawValue as typeof riskRecommendation;
  }
  const handleExportDOCX = React.useCallback(
    (event?: React.MouseEvent) => {
      if (event) {
        stopEvent(event);
      }

      if (id) {
        // Show generating state
        setIsGeneratingDoc(true);

        try {
          // Create a plain text version with proper formatting for DOCX
          const docContent = `Credit Committee Memo
================================
ID: ${id}
Date: ${new Date().toLocaleDateString()}
Company: ${mapData?.seller_name || ""}

EXECUTIVE SUMMARY
----------------
Date: ${callSchedule?.call_implementation_date ? format(new Date(callSchedule.call_implementation_date), "dd MMMM yyyy") : "N/A"}
Underwriter: ${callSchedule?.ipl_attendees?.map((attendee) => attendee.name).join(", ") || "N/A"}
Amount: ${mapInsurers?.credit_committee_amount ? `USD ${mapInsurers.credit_committee_amount}` : "N/A"}

COMPANY INFORMATION
-----------------
Company Name: ${mapData?.seller_name || ""}
Company Website: N/A
Registration Date: N/A
Industry: N/A

SUMMARY OF FINANCING REQUEST
--------------------------
Amount: ${mapInsurers?.credit_committee_amount ? `USD ${mapInsurers.credit_committee_amount}` : "N/A"}
Terms of Payment: N/A
Pricing: N/A
Desired Advance Rate: N/A

DATA SOURCES
----------
- Annual accounts
- Trade documents
- Remittance advice records
- Bank account statements

This document contains the full credit committee memo for ${mapData?.seller_name || "the company"}.
`;

          // Create a blob with the content - use text/plain instead
          const blob = new Blob([docContent], { type: "text/plain" });
          const url = URL.createObjectURL(blob);

          // Create a link and trigger download with .txt extension instead of .docx
          const link = document.createElement("a");
          link.href = url;
          link.download = `credit_memo_${id}.txt`;
          document.body.appendChild(link);
          link.click();

          // Clean up
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } catch (error) {
          console.error("Document export failed:", error);
          alert("Failed to export document. Please try again.");
        } finally {
          setIsGeneratingDoc(false);
        }
      }
    },
    [id, stopEvent, mapData, mapInsurers, callSchedule]
  );
  // Return early for loading or error states
  if (loadingComponent) return loadingComponent;
  if (errorComponent) return errorComponent;

  return (
    <Layout
      title="Credit Committee Memo"
      textHeadingOptions={{ fontWeight: "bold", level: 4 }}
      headerConfig={{
        syncAction: refetchMapSummary, // Use the refetch function from the query
        left: 10,
        mid: 0,
        right: 2,
      }}
    >
      <MemoContainer>
        <StyledPaper>
          <MemoBanner
            title="Credit Committee Memo"
            company={mapData?.seller_name}
            id={id}
          />

          <SectionCard
            title="Executive Summary"
            icon={<AssessmentIcon />}
            isFirst={true}
          >
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={tableContainerStyles}
            >
              <Table>
                <TableBody>
                  <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Date
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {mapData?.score_card_date
                        ? format(
                            new Date(mapData.score_card_date),
                            "dd MMMM yyyy"
                          )
                        : "—"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Underwriter Name
                    </TableCell>
                    <TableCell>
                      {callSchedule?.ipl_attendees
                        ?.map((attendee) => attendee.name)
                        .join(", ") || ""}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={tableContainerStyles}
            >
              <Table>
                <TableBody>
                  <ExecutiveSummaryRow
                    label="Stage"
                    value={riskRecommendation?.stage || "—"}
                  />
                </TableBody>
              </Table>
            </TableContainer>

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={tableContainerStyles}
            >
              <Table>
                <TableBody>
                  <ExecutiveSummaryRow
                    label="Source"
                    value={tabData?.deal_origin || "-"}
                  />
                  <ExecutiveSummaryRow
                    label="Responsible Sales Manager"
                    value={
                      mapInsurers?.sales_lead?.first_name ||
                      mapInsurers?.sales_lead?.last_name
                        ? `${mapInsurers.sales_lead.first_name || ""} ${mapInsurers.sales_lead.last_name || ""}`.trim()
                        : "Not Specified"
                    }
                  />
                  <ExecutiveSummaryRow
                    label="Product"
                    value={
                      mapData?.product
                        ? `${mapData?.product || ""} `.trim()
                        : "Not Specified"
                    }
                  />
                  <ExecutiveSummaryRow
                    label="Client Name"
                    value={
                      mapData?.buyer_name || mapData?.seller_name
                        ? `${mapData?.seller_name || ""}/ ${mapData?.buyer_name || ""}`.trim()
                        : "Not Specified"
                    }
                  />
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>

          <SectionCard
            title=" Financing Proposal "
            icon={<AssessmentIcon />}
            isFirst={true}
          >
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={tableContainerStyles}
            >
              <Table>
                <TableBody>
                  <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                    <TableCell sx={{ fontWeight: 600, width: "35%" }}>
                      Amount
                    </TableCell>
                    <TableCell>
                      {mapInsurers?.credit_committee_amount
                        ? `USD ${mapInsurers.credit_committee_amount}`
                        : "—"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Terms of Payment
                    </TableCell>
                    <TableCell>
                      {mapData?.payment_terms_approved
                        ? mapData?.payment_terms_approved
                        : "Not Specified"}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Desired Advance Rate
                    </TableCell>
                    <TableCell>
                      {mapData?.score_card_date &&
                      !isNaN(new Date(mapData.score_card_date).getTime())
                        ? format(
                            new Date(mapData.score_card_date),
                            "dd MMMM yyyy"
                          )
                        : "—"}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
          <SectionCard
            title=" Supplier Details "
            icon={<AssessmentIcon />}
            isFirst={true}
          >
            <SectionHeader title=" Company Information" />
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={tableContainerStyles}
            >
              <Table>
                <TableBody>
                  <ExecutiveSummaryRow
                    label="Company Name"
                    value={mapData?.seller_name || ""}
                  />
                  <ExecutiveSummaryRow
                    label="Company Website"
                    value={
                      mapData?.seller_website ? mapData?.seller_website : "-"
                    }
                  />
                  <ExecutiveSummaryRow
                    label="Registration date"
                    value={
                      mapData?.seller_incorporation_date &&
                      !isNaN(
                        new Date(mapData.seller_incorporation_date).getTime()
                      )
                        ? format(
                            new Date(mapData.seller_incorporation_date),
                            "dd MMMM yyyy"
                          )
                        : "—"
                    }
                  />

                  <ExecutiveSummaryRow
                    label="Industry"
                    value={
                      mapData?.seller_industry ? mapData?.seller_industry : "-"
                    }
                  />
                </TableBody>
              </Table>
            </TableContainer>

            <SectionHeader title="Structural overview(Company Summary) " />
            <Box sx={{ my: 2 }}>
              <Summary
                title=""
                subject="supplier"
                data={summariesData?.supplier || ""}
                readonly
              />
            </Box>

            <SectionHeader title="Business Description" />
            <Box sx={{ my: 2 }}>
              <Summary
                title=""
                subject="supplier"
                data={accountComments?.background_comments || ""}
                readonly
              />
            </Box>

            <SectionHeader title="Summary of key financial indicators" />
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                ...tableContainerStyles,
                borderRadius: "16px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>USD</strong>
                    </TableCell>
                    <TableCell>
                      <strong>{dateKeys[1]}</strong>
                    </TableCell>
                    <TableCell>
                      <strong>{dateKeys[0]}</strong>
                    </TableCell>

                    <TableCell>
                      <strong>Growth</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedFields.map((field) => {
                    const val1 = getValue(dateKeys[0], field);
                    const val2 = getValue(dateKeys[1], field);

                    const diff = getDifference(field);

                    const diffColor =
                      diff > 0 ? "green" : diff < 0 ? "red" : "inherit";
                    const formattedDiff =
                      diff > 0
                        ? `+${diff.toLocaleString()}`
                        : diff < 0
                          ? diff.toLocaleString()
                          : "0";

                    return (
                      <TableRow key={field}>
                        <TableCell>{field}</TableCell>{" "}
                        <TableCell>{val2.toLocaleString()}</TableCell>
                        <TableCell>{val1.toLocaleString()}</TableCell>
                        <TableCell style={{ color: diffColor }}>
                          {formattedDiff}%
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <SectionHeader title="Risk Analysis " />
            <Box sx={{ my: 2 }}>
              <Summary
                title=""
                subject="supplier"
                data={accountComments?.background_extra_info || ""}
                readonly
              />
            </Box>

            <SectionHeader title="External Credit Rating" />
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={tableContainerStyles}
            >
              <Table>
                <TableBody>
                  <ExecutiveSummaryRow
                    label={accountComments?.time_horizon || ""}
                    value={accountComments?.horizon_rating || ""}
                  />
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
          <SectionCard
            title=" Buyer Details "
            icon={<AssessmentIcon />}
            isFirst={true}
          >
            <SectionHeader title=" Company Information" />
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={tableContainerStyles}
            >
              <Table>
                <TableBody>
                  <ExecutiveSummaryRow
                    label="Company Name"
                    value={mapData?.buyer_name || ""}
                  />
                  <ExecutiveSummaryRow
                    label="Company Website"
                    value={
                      mapData?.buyer_website ? mapData?.buyer_website : "-"
                    }
                  />
                  <ExecutiveSummaryRow
                    label="Registration date"
                    value={
                      mapData?.buyer_incorporation_date &&
                      !isNaN(
                        new Date(mapData.buyer_incorporation_date).getTime()
                      )
                        ? format(
                            new Date(mapData.buyer_incorporation_date),
                            "dd MMMM yyyy"
                          )
                        : "—"
                    }
                  />

                  <ExecutiveSummaryRow
                    label="Industry"
                    value={
                      mapData?.buyer_industry ? mapData?.buyer_industry : "-"
                    }
                  />
                </TableBody>
              </Table>
            </TableContainer>

            <SectionHeader title="Structural overview (Company Summary)" />
            <Box sx={{ my: 2 }}>
              <Summary
                title=""
                subject="buyer"
                data={summariesData?.buyer || ""}
                readonly
              />
            </Box>

            <SectionHeader title="Business Description" />
            <Box sx={{ my: 2 }}>
              <Summary
                title=""
                subject="buyer"
                data={accountCommentsBuyer?.background_comments || ""}
                readonly
              />
            </Box>

            <SectionHeader title="Summary of key financial indicators " />
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                ...tableContainerStyles,
                borderRadius: "16px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>USD</strong>
                    </TableCell>
                    <TableCell>
                      <strong>{dateKeys[1]}</strong>
                    </TableCell>
                    <TableCell>
                      <strong>{dateKeys[0]}</strong>
                    </TableCell>

                    <TableCell>
                      <strong>Growth</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedFields.map((field) => {
                    const valBuyer1 = getValueBuyer(dateKeysBuyer[1], field);
                    const valBuyer2 = getValueBuyer(dateKeysBuyer[0], field);
                    const diff = getDifferenceBuyer(field);

                    const diffColor =
                      diff > 0 ? "green" : diff < 0 ? "red" : "inherit";
                    const formattedDiffbuyer =
                      diff > 0
                        ? `+${diff.toLocaleString()}`
                        : diff < 0
                          ? diff.toLocaleString()
                          : "0";

                    return (
                      <TableRow key={field}>
                        <TableCell>{field}</TableCell>
                        <TableCell>{valBuyer1.toLocaleString()}</TableCell>
                        <TableCell>{valBuyer2.toLocaleString()}</TableCell>
                        <TableCell style={{ color: diffColor }}>
                          {formattedDiffbuyer}%
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <SectionHeader title="Risk Analysis" />
            <Box sx={{ my: 2 }}>
              <Summary
                title=""
                subject="buyer"
                data={accountCommentsBuyer?.background_extra_info || ""}
                readonly
              />
            </Box>

            <SectionHeader title="External Credit Rating" />
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={tableContainerStyles}
            >
              <Table>
                <TableBody>
                  <ExecutiveSummaryRow
                    label={accountCommentsBuyer?.time_horizon || ""}
                    value={accountCommentsBuyer?.horizon_rating || ""}
                  />
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
          <SectionCard
            title=" List of data sources/documents "
            icon={<AssessmentIcon />}
            isFirst={true}
          >
            <Box sx={{ my: 2 }}>
              <Summary
                title=" "
                subject="buyer"
                data={summariesData?.deal || ""}
                readonly
              />
            </Box>
          </SectionCard>

          <SectionCard
            title="Key Developments and Case-Specific Factors"
            icon={<AssessmentIcon />}
          >
            <Box sx={{ my: 2 }}>
              <Summary
                title=" "
                subject="buyer"
                data={mapData?.transaction_extra_info || ""}
                readonly
              />
            </Box>
          </SectionCard>
          <SectionCard
            title="Case Strengths & Weaknesses"
            icon={<AssessmentIcon />}
          >
            <Box sx={{ my: 2 }}>
              <Summary
                title=" "
                subject="buyer"
                data={mapData?.transaction_comments || ""}
                readonly
              />
            </Box>
          </SectionCard>
          <SectionCard
            title=" IPL Credit Score "
            icon={<AssessmentIcon />}
            isFirst={true}
          >
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={tableContainerStyles}
            >
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Provider</TableCell>
                    <TableCell>Incomlend</TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Report Date
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.875rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {mapData?.score_card_date
                        ? format(
                            new Date(mapData.score_card_date),
                            "dd MMMM yyyy"
                          )
                        : "—"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Score</TableCell>
                    <TableCell>
                      {mapData?.approved_ipl_risk_score
                        ? mapData?.approved_ipl_risk_score
                        : "Not Specified"}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
          <SectionCard
            title="Further Comments by Risk Team"
            icon={<AssessmentIcon />}
          >
            <Box sx={{ my: 2 }}>
              <Summary
                title=" "
                subject="buyer"
                data={mapData?.score_card_comments || ""}
                readonly
              />
            </Box>
          </SectionCard>
          <SectionCard title="Recommendation" icon={<AssessmentIcon />}>
            <Box sx={{ mb: 4 }}>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={tableContainerStyles}
              >
                <Table>
                  <TableBody>
                    <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                      <TableCell sx={{ fontWeight: 600, width: "35%" }}>
                        Credit Limit
                      </TableCell>
                      <TableCell>
                        {riskRecommendation?.credit_limit || "—"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Advance Rate
                      </TableCell>
                      <TableCell>
                        {riskRecommendation?.advance_rate || "—"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Security or Guarantees
                      </TableCell>
                      <TableCell>
                        {riskRecommendation?.guarantees || "—"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Conditions Precedent{" "}
                      </TableCell>
                      <TableCell>
                        {riskRecommendation?.conditions_precedent || "—"}
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Monitoring Schedule{" "}
                      </TableCell>
                      <TableCell>
                        {riskRecommendation?.monitoring_schedule || "—"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Repayment History{" "}
                      </TableCell>
                      <TableCell>
                        {riskRecommendation?.repayment_history || "—"}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </SectionCard>

          <SectionCard
            title="Follow up questions that need to be resolved"
            icon={<AssessmentIcon />}
          >
            <Box sx={{ my: 2 }}>
              <Summary
                title=" "
                subject="buyer"
                data={riskRecommendation?.risk_questions || ""}
                readonly
              />
            </Box>
          </SectionCard>
        </StyledPaper>
      </MemoContainer>
      <Box
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          display: "flex",
          gap: 2,
          zIndex: 1000,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleExportPDF}
          startIcon={<PdfIcon />}
          disabled={isGeneratingDoc}
          sx={{
            borderRadius: "20px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
            padding: "6px 16px",
            fontSize: "0.875rem",
            fontWeight: 500,
            textTransform: "none",
            letterSpacing: "0.2px",
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            "&:hover": {
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.16)",
              transform: "translateY(-1px)",
            },
          }}
        >
          {isGeneratingDoc ? "Generating..." : "Download PDF"}
        </Button>
      </Box>
    </Layout>
  );
};

export default CreditCommitteeMemo;
